import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import T from 'utils/T';
import ex from 'utils/ex';
import Navbar from 'components/Navbar';

import useWindowResize from 'hooks/events/useWindowResize';

import useQueueRequest from 'hooks/communication/useQueueRequest';
import HOST_API from 'constants/HOST_API';
import useTabFocus from 'hooks/common/useTabFocus';
import useFloatings from 'hooks/layers/useFloatings';
import VBox from 'components/common/VBox';
import Header from 'components/header';
import useCategorizedPages from 'hooks/app/useCategorizedPages';
import PageOutlet from 'components/pages/PageOutlet';
import PageFloatings from 'components/floatings/PageFloatings';

import './App.css';
import useMessageBox from 'hooks/app/useMessageBox';
import usePdfDisabled from 'hooks/app/usePdfDisabled';
import AgpBase from './AgpBase';

const category = 'App';
export default function App(props) {
  useTabFocus();
  useWindowResize();
  const pages = useCategorizedPages(category);

  useQueueRequest(HOST_API);
  const {
    inert,
    floatings,
  } = useFloatings(category);

  const {
    open,
    update,
    close,
  } = useMessageBox('App');

  const disabled = usePdfDisabled();
  const [downloading, setDownloading] = Recoil.useRecoilState(AppStore.assets.data('PdfDownloading'));
  React.useEffect(() => {
    if (downloading.error) {
      setDownloading(() => ({ uninitialized: true }));
      open({
        id: 'pdf',
        title: T('PDF 생성 실패'),
        contents: (
          <VBox>
            <div>
              {T('PDF 생성을 실패했습니다.')}
            </div>
            <div>
              {disabled}
            </div>
          </VBox>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => {
            close('pdf');
          },
        },
      });
    } else if (!downloading.uninitialized) {
      const timeoutId = setTimeout(() => {
        setDownloading(() => ({ error: true }));
      }, 30000);
      return () => clearTimeout(timeoutId);
    }
  }, [close, disabled, downloading.error, downloading.uninitialized, open, setDownloading]);

  const pdfProcessing = React.useMemo(() => {
    if(!(downloading && downloading.processing)) return null;
    return (
      <React.Fragment>
        <VBox {...ex('PDFProcessing')}>
          <div>
            {T('PDF 생성중입니다')}
          </div>
          <div>
            {disabled}
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" width={32} height={32} style={{ shapeRendering: 'auto', display: 'block' }}>
            <g data-idx={1}>
              <g transform="matrix(1,0,0,1,0,0)" data-idx={2}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={3} opacity="0.553336">
                </rect>
              </g>
              <g transform="matrix(0.8660254037844387,0.49999999999999994,-0.49999999999999994,0.8660254037844387,31.698729810778058,-18.30127018922194)" data-idx={5}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={6} opacity="0.636669">
                </rect>
              </g>
              <g transform="matrix(0.5000000000000001,0.8660254037844386,-0.8660254037844386,0.5000000000000001,68.30127018922192,-18.30127018922194)" data-idx={8}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={9} opacity="0.720002">
                </rect>
              </g>
              <g transform="matrix(6.123233995736766e-17,1,-1,6.123233995736766e-17,100,0)" data-idx={11}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={12} opacity="0.803336">
                </rect>
              </g>
              <g transform="matrix(-0.4999999999999998,0.8660254037844387,-0.8660254037844387,-0.4999999999999998,118.30127018922192,31.69872981077805)" data-idx={14}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={15} opacity="0.886669">
                </rect>
              </g>
              <g transform="matrix(-0.8660254037844387,0.49999999999999994,-0.49999999999999994,-0.8660254037844387,118.30127018922194,68.30127018922194)" data-idx={17}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={18} opacity="0.970002">
                </rect>
              </g>
              <g transform="matrix(-1,1.2246467991473532e-16,-1.2246467991473532e-16,-1,100,100)" data-idx={20}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={21} opacity="0.053336">
                </rect>
              </g>
              <g transform="matrix(-0.8660254037844386,-0.5000000000000001,0.5000000000000001,-0.8660254037844386,68.30127018922192,118.30127018922194)" data-idx={23}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={24} opacity="0.136669">
                </rect>
              </g>
              <g transform="matrix(-0.5000000000000004,-0.8660254037844385,0.8660254037844385,-0.5000000000000004,31.698729810778097,118.30127018922195)" data-idx={26}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={27} opacity="0.220002">
                </rect>
              </g>
              <g transform="matrix(-1.8369701987210297e-16,-1,1,-1.8369701987210297e-16,7.105427357601002e-15,100)" data-idx={29}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={30} opacity="0.303336">
                </rect>
              </g>
              <g transform="matrix(0.5000000000000001,-0.8660254037844386,0.8660254037844386,0.5000000000000001,-18.30127018922194,68.30127018922192)" data-idx={32}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={33} opacity="0.386669">
                </rect>
              </g>
              <g transform="matrix(0.8660254037844384,-0.5000000000000004,0.5000000000000004,0.8660254037844384,-18.30127018922194,31.698729810778104)" data-idx={35}>
                <rect fill="#ffffff" height={4} width={4} ry={0} rx={0} y={24} x={48} data-idx={36} opacity="0.470002">
                </rect>
              </g>
            </g>
          </svg>
        </VBox>
        <PageOutlet
          {...{
            renderAll: true,
            inert,
            category,
            pages,
          }}
        />
      </React.Fragment>
    );
  }, [disabled, downloading, inert, pages]);

  return (
    <div
      {...ex('App', props)}
    >
      <Navbar {...{
        category,
        inert,
      }} />
      <AgpBase />
      <VBox full>
        <Header
          {...props}
          {...{
            inert,
          }}
        />
        <VBox full>
          <PageOutlet
            {...{
              inert,
              category,
              pages: downloading.processing ? [] : pages,
            }}
          />
          {downloading && downloading.error && (
            <div {...ex('PDFProcessing')}>
              {T('PDF 생성을 실패했습니다.')}
            </div>
          )}
          {pdfProcessing}
        </VBox>
      </VBox>
      <PageFloatings
        {...{
          category,
          floatings,
        }}
      />
    </div>
  );
}
